import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useImageStore } from '../../lib/stores/image';
import { PHOTOS_REQUIRED } from './Question';
import { reportStatus, useScheduledReportStore } from '../../lib/stores/scheduledReport';

const propTypes = {
  questions: PropTypes.array.isRequired,
  questionSelect: PropTypes.func.isRequired,
  current: PropTypes.object,
  inspectionMoment: PropTypes.number,
};

function QuestionsProgress({ questionSelect, current, inspectionMoment = 0 }) {

  const { scheduledReports } = useScheduledReportStore();
  const { images } = useImageStore();

  /**
   * This function gets the completion status of the question.
   * 
   * @param {object} question The question object.
   * @returns {'missingImage' | 'missingAnswer' | 'complete'} The completion status of the answer.
   */
  const getQuestionStatus = useCallback((question) => {

    const loadedImages = Array.from(images.values()) || [];

    if(question.photos === PHOTOS_REQUIRED && !loadedImages.some(image => image.answerId === question.id)) {
      return 'missingImage';
    }

    if(!question.answer) {
      return 'missingAnswer';
    }

    return 'complete';

  }, [images]);

  /**
   * Questions filtered by moment
   * @type {Array} momentQuestions
   */
  const momentQuestions = useMemo(() => {
    
    const activeReport = scheduledReports.find((report) => report.status === reportStatus.ACTIVE);
    if(!activeReport) return []

    const answers = activeReport.data.answers || [];
    return answers.filter((answer) => answer.moment === inspectionMoment && !answer.parent_question_id);
  }, [scheduledReports, inspectionMoment]);

  /**
   * Last complete index
   * @type {Number} lastCompleteIndex
   */
  const lastCompleteIndex = useMemo(() => {

    let lastIndex = -1;
    
    momentQuestions.forEach((question, index) => {
      if(getQuestionStatus(question) === 'complete') {
        lastIndex = index;
      }
    });

    return lastIndex;
  }, [momentQuestions, images]);

  /**
   * Determines if the question is currently selected
   * @param {Object} question - The current question
   * @returns {Boolean} - Whether the question is the current one
   */
  const isCurrentQuestion = (question) => {
    if (!current) return false;
    return current.parent_question_id 
      ? question.question_id === current.parent_question_id
      : question.id === current.id;
  };

  /**
   * Determines the click handler based on the completion state
   * @param {Object} question - The current question
   * @param {Number} index - The index of the current question
   * @returns {Function|null} - The click handler or null if not clickable
   */
  const getClickHandler = (question, index) => {
    const status = getQuestionStatus(question);
    const isClickable = status === 'complete' || index - 1 === lastCompleteIndex;
    return isClickable ? () => questionSelect(question) : null;
  };

  /**
   * Returns the CSS class for the current question
   * @param {Object} question - The current question
   * @param {Boolean} isCurrent - Whether the question is the current one
   * @returns {String} - The computed class names
   */
  const getClassNames = (question) => {
    const isCurrent = isCurrentQuestion(question);
    const status = getQuestionStatus(question);

    if(isCurrent) return 'current'; // Active
    if(status === 'complete') return 'active'; // complete
    return 'inactive'; // missingImage or missingAnswer
  };

  return (
    <div id="progress-bar" data-qty="40" data-current="26">
      {momentQuestions.map((question, index) => {
        const classNames = getClassNames(question);
        const onClick = getClickHandler(question, index);

        return (
          <span
            key={question.id}
            onClick={onClick}
            className={classNames}
          />
        );
      })}
    </div>
  );
}

QuestionsProgress.propTypes = propTypes;

export default QuestionsProgress;
